import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { TextInput, Image } from "hesus-ui-kit";
import { useNavigate, Link } from "react-router-dom";
import PulseLoader from "react-spinners/PulseLoader";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import addressIcon from "@icons/address-icon.svg";
import phoneIcon from "@icons/phone-icon.svg";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import { CENTER_LIST_ENDPOINT } from "@utils/constants";
import { loadAllCenter } from "./CenterList.actionCreators";
import { setAllCenterLoadingState } from "./CenterList.slice";
import PaginationData from "@shared/baseUI/PaginationData/PaginationData";
import CenterAction from "./components/CenterAction/CenterAction";
import { searchInputStyles } from "@utils/extra-style";
import {
  OuterWrapper,
  FlexRow,
  FilterFlexRow,
  SectionContainer,
  GridRow,
  ProductContainer,
  ButtonWrapper,
  CreateButton,
  FilterOuter,
  GlobalSearch,
  CenterProductCount,
  GridLoaderItem,
} from "./CenterList.styled";

import "./CenterList.scss";
import { textReplace, address } from "@utils/utils";

const ViewData = (props) => {
  const { t, countryId, companyId } = props;

  const [activeStatus, setActiveStatus] = useState(1);
  const [data, setData] = useState([]);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [pageStart, setPageStart] = useState(0);
  const [pageSize, setPageSize] = useState(9);
  const [search, setSearch] = useState("");
  const [sort, setSortOption] = useState({ dir: "asc", column: "id" });

  const history = useNavigate();
  const generalParams = useGetGeneralParameters();
  const { countryList } = generalParams;

  const onCentreCreateClick = () => {
    history(`/company/create?parent=${companyId}`);
  };
  let dataURL = textReplace(CENTER_LIST_ENDPOINT, "{companyId}", companyId);

  const dispatch = useDispatch();

  const centerListByIds = useSelector((state) => state.centerList.byId);
  const centerListAllIds = useSelector((state) => state.centerList.allIds);
  const filteredRecords = useSelector(
    (state) => state.centerList.filteredRecords
  );

  const isCenterListLoading = useSelector(
    (state) => state.centerList.isLoading
  );

  useEffect(() => {
    setLoading(isCenterListLoading);
  }, [isCenterListLoading]);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    search,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // save request data,
    setRequestData({ pageSize, pageStart, search, sort, appliedFilters });
    // Set the loading state
    setLoading(true);

    dispatch(setAllCenterLoadingState());
    dispatch(
      loadAllCenter({
        dataURL,
        pageSize,
        currentPage,
        sort,
        search,
        appliedFilters,
        status: 1,
      })
    );
  };

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
  }, [isRefreshDataAgain]);

  useEffect(() => {
    if (!loading) {
      fetchRecords({
        pageSize,
        pageStart,
        search,
        sort,
        appliedFilters: {
          status: activeStatus,
        },
        currentPage,
      });
    }
  }, [
    currentPage,
    pageSize,
    pageStart,
    activeStatus,
    search,
    isRefreshDataAgain,
  ]);

  useEffect(() => {
    if (centerListAllIds.length > 0) {
      const responseData = centerListAllIds.map((id) => {
        const tempData = { ...centerListByIds[id] };
        return tempData;
      });
      setData([...responseData]);
      setPageCount(filteredRecords);
      setLoading(false);
    } else {
      setData([]);
      setPageCount(0);
      setLoading(false);
    }
  }, [t, centerListAllIds, centerListByIds, filteredRecords]);

  const changeStatus = (label) => {
    if (label === "status") {
      const newStatus = activeStatus === 1 ? 2 : 1;
      setActiveStatus(newStatus);
      setSortOption(
        newStatus
          ? { dir: "desc", column: "status" }
          : { dir: "desc", column: "id" }
      );
    }
    setCurrentPage(1);
  };

  const globalSearchFilter = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  return (
    <>
      <OuterWrapper>
        <FlexRow>
          <SectionContainer>
            <div>
              <FilterFlexRow>
                <FilterOuter>
                  <div className="status__filters-wrapper">
                    <Image
                      src={activeStatus === 1 ? enableIcon : disableIcon}
                      alt={t("CENTRE_LIST_TOGGLE_ACTIVE_CENTRES")}
                      title={t("CENTRE_LIST_TOGGLE_ACTIVE_CENTRES")}
                      styles={{ marginLeft: "7px" }}
                      onClick={() => changeStatus("status")}
                      className="center-list__image-icon"
                    />
                    <label className="label center-list__active-label">
                      {t("CENTRE_LIST_TOGGLE_ACTIVE_CENTRES")}
                    </label>
                  </div>
                </FilterOuter>
                <GlobalSearch>
                  <div className="search-box">
                    <TextInput
                      id="center-list-search"
                      type="search"
                      inputClass="search-box-default-input"
                      value={search || ""}
                      onChange={(e) => globalSearchFilter(e)}
                      placeholder=""
                      name="global-filter"
                      inputStyles={searchInputStyles}
                    />
                  </div>
                </GlobalSearch>
                <ButtonWrapper>
                  <CreateButton onClick={onCentreCreateClick}>{`${t(
                    "ACTION_NEW_CENTRE"
                  )}`}</CreateButton>
                </ButtonWrapper>
              </FilterFlexRow>
            </div>
            <div>
              {loading && (
                <GridRow column="1fr">
                  <GridLoaderItem>
                    <PulseLoader width="100%" color="#1bace4" />
                  </GridLoaderItem>
                </GridRow>
              )}
              <GridRow column="repeat(auto-fit, minmax(300px, 420px));">
                {data.length !== "" &&
                  data.map((data, index) => {
                    return (
                      <ProductContainer key={index}>
                        <GridRow column="70% auto" gap="0rem">
                          <span>
                            <Link
                              to={`/company/view/${data.id}/general-info`}
                              width="220%"
                              className="center-list__company-link"
                              style={{ opacity: !data.isActive ? ".2" : 1 }}
                            >
                              {"#" +
                                data?.id +
                                (data.name ? " - " + data.name : "")}
                            </Link>
                          </span>
                          <CenterAction
                            data={data}
                            companyId={companyId}
                            onRefreshData={setRefreshState}
                          />
                        </GridRow>
                        <GridRow
                          column="30px auto"
                          opacity={!data.isActive ? ".2" : 1}
                        >
                          <Image
                            src={addressIcon}
                            alt="address"
                            className="center-list__image-icon"
                          />
                          <span
                            dangerouslySetInnerHTML={{
                              __html: address(
                                countryId,
                                data.shippingAddress,
                                countryList
                              ),
                            }}
                          ></span>
                        </GridRow>

                        <GridRow
                          column="30px auto"
                          opacity={!data.isActive ? ".2" : 1}
                        >
                          <>
                            <Image
                              src={phoneIcon}
                              alt="phone"
                              className="center-list__image-icon"
                            />
                            <span>{data.phone}</span>
                          </>
                        </GridRow>
                        {data?.productsCount !== 0 && (
                          <CenterProductCount
                            opacity={!data.isActive ? ".2" : 1}
                          >
                            {data?.productsCount} {t("CENTRE_PRODUCT_COUNT")}
                          </CenterProductCount>
                        )}
                        {data?.productsCount === 0 && (
                          <CenterProductCount
                            opacity={!data.isActive ? ".2" : 1}
                          >
                            {t("CENTRE_NO_PRODUCT")}
                          </CenterProductCount>
                        )}
                      </ProductContainer>
                    );
                  })}
              </GridRow>
              {(data.length === "" || data.length === 0) && (
                <GridRow column="1fr">{t("NO_CENTRE")}</GridRow>
              )}
            </div>
          </SectionContainer>
        </FlexRow>

        {data.length !== 0 && (
          <PaginationData
            pageCount={parseInt(pageCount, 10)}
            pageSize={requestData.pageSize}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageStart={pageStart}
            setPageStart={setPageStart}
            setPageSize={setPageSize}
          />
        )}
      </OuterWrapper>
    </>
  );
};

ViewData.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
};

export default withTranslation()(ViewData);
